import type {
	ReactionsConfluenceSaveReactionMutation,
	ReactionsDeleteReactionMutation,
	ReactionsMutationResultsData,
} from '@confluence/reactions/entry-points/reactionTypes.ts';

import type { EditCommentUpdateCommentMutation_Mutation } from '../__types__/EditCommentUpdateCommentMutation.ts';

// Function overloads for better type safety
export function getOptimisticResponse(params: {
	ari: string;
	containerAri: string;
	emojiId: string;
	action: 'add';
}): ReactionsConfluenceSaveReactionMutation;

export function getOptimisticResponse(params: {
	ari: string;
	containerAri: string;
	emojiId: string;
	action: 'delete';
}): ReactionsDeleteReactionMutation;

export function getOptimisticResponse({
	ari,
	containerAri,
	emojiId,
	action,
}: {
	ari: string;
	containerAri: string;
	emojiId: string;
	action: 'add' | 'delete';
}): ReactionsMutationResultsData {
	if (action === 'add') {
		return {
			__typename: 'Mutation',
			confluence_addReaction: {
				__typename: 'ConfluenceReactionPayload',
				ari,
				containerAri,
				emojiId,
			},
		} as ReactionsConfluenceSaveReactionMutation;
	} else {
		return {
			__typename: 'Mutation',
			deleteReaction: {
				__typename: 'SaveReactionResponse',
				ari,
				containerAri,
				emojiId,
			},
		} as ReactionsDeleteReactionMutation;
	}
}

/**
 * Builds an optimistic response for EditCommentUpdateCommentMutation so that the comment body,
 * version timestamp, and author are updated in the cache immediately when a user saves an inline
 * comment edit — making the editor close instantly and showing the "(edited)" label before the
 * network round-trip completes.
 *
 * NOTE: version.number is set to 2 so that (version.number || 0) > 1 evaluates to true,
 * which renders the "(edited)" label immediately. The real version.number is written by the
 * cache update callback once the server responds.
 *
 * NOTE: Moving this to @confluence/comments-common is deferred — there is a circular dependency
 * (comments-common -> inline-comments-common) that prevents the move without a larger refactor.
 */
export function getEditCommentOptimisticResponse({
	commentId,
	adfString,
	sessionDisplayName,
}: {
	commentId: string;
	adfString: string;
	sessionDisplayName: string | null;
}): EditCommentUpdateCommentMutation_Mutation {
	return {
		updateComment: {
			__typename: 'Comment',
			id: commentId,
			body: {
				__typename: 'DocumentBody',
				value: adfString,
			},
			version: {
				__typename: 'Version',
				number: 2,
				when: new Date().toISOString(),
				by: {
					__typename: 'KnownUser',
					displayName: sessionDisplayName,
				},
			},
		},
	};
}
